/**
 * O "Voltar" do navegador com edição não salva (E202.F2.C05, F-E202.F2-05).
 *
 * O app usa o `BrowserRouter`, sem o bloqueio de navegação do roteador de dados, e o `popstate` não se
 * cancela. Então, enquanto a edição está suja, a mesa põe uma entrada de guarda no histórico, com o
 * mesmo endereço: o "Voltar" sai dela e cai na própria mesa (o roteador não troca de tela), e a mesa
 * pergunta antes de sair de verdade. "Ficar" repõe a guarda. Quando a edição deixa de estar suja, a
 * guarda sai do histórico, para o "Voltar" seguinte não parecer não funcionar.
 *
 * As âncoras da própria mesa (o índice "Nesta mesa") empilham entradas com o mesmo endereço; voltar
 * por elas não pergunta nada. Só pergunta quem sai da guarda para a entrada da mesa que a recebeu.
 */
import { useCallback, useEffect, useRef } from 'react';

const MARCA = 'guardaDaMesa';

type Estado = Record<string, unknown> | null;

function estadoAtual(): Estado {
  return (window.history.state as Estado) ?? null;
}

/** A entrada atual é a guarda: sair por um link troca a guarda pelo destino (`replace`). */
export function naGuarda(): boolean {
  return estadoAtual()?.[MARCA] === true;
}

export function useGuardaDoHistorico(ativa: boolean, aoVoltar: () => void) {
  // A chave da entrada da mesa que recebeu a guarda: voltar até ela é sair da mesa.
  const origem = useRef<unknown>(null);
  // Saindo pelo diálogo: a guarda não volta a ser tirada no caminho (a navegação já cuida dela).
  const saindo = useRef(false);
  // A mesa tirou a guarda (`history.back()`, que chega depois): esse `popstate` não é a pessoa saindo.
  const tirando = useRef(false);
  const aoVoltarAtual = useRef(aoVoltar);
  aoVoltarAtual.current = aoVoltar;

  const por = useCallback(() => {
    const estado = estadoAtual();
    origem.current = estado?.key ?? null;
    window.history.pushState({ ...(estado ?? {}), [MARCA]: true }, '', window.location.href);
  }, []);

  useEffect(() => {
    if (!ativa) return undefined;
    saindo.current = false;
    if (!naGuarda()) {
      tirando.current = false;
      por();
    }
    const aoPop = (e: PopStateEvent) => {
      const estado = (e.state as Estado) ?? null;
      if (tirando.current) {
        tirando.current = false;
        if (!naGuarda()) por();
        return;
      }
      if (estado?.[MARCA] === true) return;
      if ((estado?.key ?? null) === origem.current && !saindo.current) aoVoltarAtual.current();
    };
    window.addEventListener('popstate', aoPop);
    return () => {
      window.removeEventListener('popstate', aoPop);
      if (saindo.current || !naGuarda()) return;
      tirando.current = true;
      window.history.back();
    };
  }, [ativa, por]);

  return {
    /** "Ficar" depois do "Voltar": a guarda volta ao histórico. */
    repor: por,
    /** A saída pelo diálogo começou (`true`) ou foi recusada e a pessoa ficou (`false`). */
    marcarSaida: (sim: boolean) => {
      saindo.current = sim;
    },
  };
}
